import { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useSearchParams } from "react-router-dom";
import { AppLayout } from "@/components/AppLayout";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { FloatField, FloatWrapper } from "@/components/ui/FormField";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { EmployeeCombobox } from "@/components/ui/EmployeeCombobox";
import { ListSkeleton } from "@/components/PageSkeleton";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/contexts/AuthContext";
import {
  ShieldAlert,
  Shield,
  MessageSquare,
  AlertTriangle,
  Send,
} from "lucide-react";
import {
  RaiseConcernMessage,
  RaiseConcernCase,
  RaiseConcernStatus,
  apiGetMyConcernCases,
  getTenantSlug,
  apiReplyToConcern,
  apiGetEmployees,
  apiSubmitConcern,
  apiCancelConcern,
  apiRequestConcernReview,
} from "@/lib/api";

// ── Constants ─────────────────────────────────────────────────────────────────

const OPEN_CASE_STATUSES: RaiseConcernStatus[] = [
  "submitted",
  "in_review",
  "assigned",
];

const CASE_STATUS_CONFIG: Record<RaiseConcernStatus, { label: string; color: string }> = {
  submitted: {
    label: "status_submitted",
    color: "bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300",
  },
  in_review: {
    label: "status_in_review",
    color: "bg-yellow-100 text-yellow-700 dark:bg-yellow-900/40 dark:text-yellow-300",
  },
  assigned: {
    label: "status_assigned",
    color: "bg-orange-100 text-orange-700 dark:bg-orange-900/40 dark:text-orange-300",
  },
  resolved: {
    label: "status_resolved",
    color: "bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300",
  },
  closed: {
    label: "status_closed",
    color: "bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400",
  },
  cancelled: {
    label: "status_cancelled",
    color: "bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300",
  },
};

function parseConcernMessages(raw: string | null): RaiseConcernMessage[] {
  if (!raw?.trim()) return [];
  try {
    const parsed = JSON.parse(raw);
    if (Array.isArray(parsed)) return parsed;
  } catch {
    return [];
  }
  return [];
}

// ── Chat bubble renderer ───────────────────────────────────────────────────────

function ConcernThread({ rawMessages }: { rawMessages: string | null }) {
  const { t } = useTranslation();
  const bottomRef = useRef<HTMLDivElement>(null);
  const messages = parseConcernMessages(rawMessages);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [rawMessages]);

  const getLabel = (message: RaiseConcernMessage) => {
    if (message.kind === "case_cancelled")
      return t("raiseConcern.caseCancelledHistory", { defaultValue: "Concern cancelled" });
    if (message.kind === "review_requested")
      return t("raiseConcern.reviewRequestedHistory", { defaultValue: "Review requested" });
    if (message.sender === "submitter")
      return t("raiseConcern.you", { defaultValue: "You" });
    return t("raiseConcern.hrLabel", { defaultValue: "HR" });
  };

  if (messages.length === 0) {
    return (
      <div className="flex items-center justify-center h-full py-8">
        <p className="text-xs text-muted-foreground text-center">
          {t("raiseConcern.noConversationYet", { defaultValue: "No messages yet." })}
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-2.5">
      {messages.map((message) => {
        const isActionMessage =
          message.kind === "case_cancelled" || message.kind === "review_requested";

        if (isActionMessage) {
          return (
            <div
              key={message.id}
              className="mx-auto max-w-[90%] rounded-xl border border-amber-200 bg-amber-50/80 px-3 py-2 dark:border-amber-800 dark:bg-amber-950/20 text-center"
            >
              <p className="text-xs font-semibold text-amber-800 dark:text-amber-300">
                {getLabel(message)}
              </p>
              {message.text && (
                <p className="mt-0.5 text-xs text-amber-700 dark:text-amber-200 whitespace-pre-wrap">
                  {message.text}
                </p>
              )}
              <span className="mt-1 block text-[10px] text-amber-600/70 dark:text-amber-300/60">
                {new Date(message.timestamp).toLocaleString()}
              </span>
            </div>
          );
        }

        const isMe = message.sender === "submitter";

        return (
          <div
            key={message.id}
            className={`flex flex-col ${isMe ? "items-end" : "items-start"}`}
          >
            <div
              className={`px-3.5 py-2.5 text-sm max-w-[82%] shadow-sm ${
                isMe
                  ? "bg-primary text-primary-foreground rounded-2xl rounded-tr-sm"
                  : "bg-background border rounded-2xl rounded-tl-sm"
              }`}
            >
              <p className="whitespace-pre-wrap leading-relaxed">{message.text}</p>
            </div>
            <span className="text-[10px] text-muted-foreground mt-1 px-1.5">
              {getLabel(message)} · {new Date(message.timestamp).toLocaleString()}
            </span>
          </div>
        );
      })}
      <div ref={bottomRef} />
    </div>
  );
}

// ── Main component ─────────────────────────────────────────────────────────────

export default function RaiseConcern() {
  const { t } = useTranslation();
  const { toast } = useToast();
  const { user } = useAuth();
  const qc = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();

  // ── Submit new concern ───────────────────────────────────────────────────────
  const [createOpen, setCreateOpen] = useState(false);
  const [reportedEmployeeId, setReportedEmployeeId] = useState("");
  const [description, setDescription] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const { data: allEmployees = [] } = useQuery({
    queryKey: ["employees"],
    queryFn: apiGetEmployees,
    enabled: createOpen,
  });

  // Exclude the current user's own employee record from the reported-employee list
  const selectableEmployees = allEmployees.filter(
    (e) => user?.employeeId == null || String(e.id) !== String(user.employeeId)
  );

  const handleSubmitConcern = async () => {
    if (!reportedEmployeeId || !description.trim()) {
      toast({ title: t("raiseConcern.errorRequired"), variant: "destructive" });
      return;
    }
    setSubmitting(true);
    try {
      await apiSubmitConcern({
        reportedEmployeeId: Number(reportedEmployeeId),
        description: description.trim(),
      });
      toast({
        title: t("raiseConcern.submitted"),
        description: t("raiseConcern.submittedDesc"),
      });
      setCreateOpen(false);
      setReportedEmployeeId("");
      setDescription("");
      qc.invalidateQueries({ queryKey: ["my-concern-cases", getTenantSlug()] });
    } catch (e: any) {
      toast({ title: "Error", description: e.message, variant: "destructive" });
    } finally {
      setSubmitting(false);
    }
  };

  // ── Employee: my submitted cases ─────────────────────────────────────────────
  const { data: myCases = [], isLoading: casesLoading } = useQuery<RaiseConcernCase[]>({
    queryKey: ["my-concern-cases", getTenantSlug()],
    queryFn: apiGetMyConcernCases,
  });

  const [selectedCase, setSelectedCase] = useState<RaiseConcernCase | null>(null);
  const [replyText, setReplyText] = useState("");
  const [sendingReply, setSendingReply] = useState(false);
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [cancelReason, setCancelReason] = useState("");
  const [cancellingCase, setCancellingCase] = useState(false);
  const [reviewDialogOpen, setReviewDialogOpen] = useState(false);
  const [reviewRequestText, setReviewRequestText] = useState("");
  const [requestingReview, setRequestingReview] = useState(false);

  // Auto-open employee case from notification link (?caseId=N)
  useEffect(() => {
    const caseIdParam = searchParams.get("caseId");
    if (caseIdParam && myCases.length > 0) {
      const target = myCases.find((c) => c.id === Number(caseIdParam));
      if (target) {
        setSelectedCase(target);
        setReplyText("");
        setSearchParams(
          (prev) => { const next = new URLSearchParams(prev); next.delete("caseId"); return next; },
          { replace: true }
        );
      }
    }
  }, [searchParams, myCases, setSearchParams]);

  useEffect(() => {
    if (!selectedCase) return;
    const refreshedCase = myCases.find((c) => c.id === selectedCase.id);
    if (refreshedCase) setSelectedCase(refreshedCase);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [myCases, selectedCase?.id]);

  const refreshConcernQueries = async () => {
    await Promise.all([
      qc.invalidateQueries({ queryKey: ["my-concern-cases", getTenantSlug()] }),
      qc.invalidateQueries({ queryKey: ["all-concern-cases", getTenantSlug()] }),
      qc.invalidateQueries({ queryKey: ["concern-open-count", getTenantSlug()] }),
      qc.invalidateQueries({ queryKey: ["concern-open-count"] }),
    ]);
  };

  const handleSendReply = async () => {
    if (!selectedCase || !replyText.trim()) return;
    setSendingReply(true);
    try {
      await apiReplyToConcern(selectedCase.id, replyText.trim());
      setReplyText("");
      toast({ title: t("raiseConcern.additionalInfoSent", { defaultValue: "Information sent" }) });
      await refreshConcernQueries();
    } catch (e: any) {
      toast({ title: "Error", description: e.message, variant: "destructive" });
    } finally {
      setSendingReply(false);
    }
  };

  const handleCancelConcern = async () => {
    if (!selectedCase) return;
    setCancellingCase(true);
    try {
      await apiCancelConcern(selectedCase.id, cancelReason.trim() || undefined);
      toast({
        title: t("raiseConcern.cancelSuccess", { defaultValue: "Concern cancelled" }),
        description: t("raiseConcern.cancelSuccessDesc", {
          defaultValue: "The case was marked as cancelled and HR can still see its history.",
        }),
      });
      setCancelDialogOpen(false);
      setCancelReason("");
      await refreshConcernQueries();
    } catch (e: any) {
      toast({ title: "Error", description: e.message, variant: "destructive" });
    } finally {
      setCancellingCase(false);
    }
  };

  const handleRequestReview = async () => {
    if (!selectedCase) return;
    setRequestingReview(true);
    try {
      await apiRequestConcernReview(selectedCase.id, reviewRequestText.trim() || undefined);
      toast({
        title: t("raiseConcern.reviewRequestSuccess", { defaultValue: "Review requested" }),
        description: t("raiseConcern.reviewRequestSuccessDesc", {
          defaultValue: "HR was notified and the case moved back to In Review.",
        }),
      });
      setReviewDialogOpen(false);
      setReviewRequestText("");
      await refreshConcernQueries();
    } catch (e: any) {
      toast({ title: "Error", description: e.message, variant: "destructive" });
    } finally {
      setRequestingReview(false);
    }
  };

  // ── Main render ──────────────────────────────────────────────────────────────

  return (
    <AppLayout>
      <div className="space-y-6 w-full max-w-3xl mx-auto">
        {/* Page header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="h-9 w-9 rounded-xl bg-red-500/10 flex items-center justify-center shrink-0">
              <ShieldAlert className="h-5 w-5 text-red-600" />
            </div>
            <div>
              <h1 className="text-2xl font-bold">{t("raiseConcern.title", { defaultValue: "Raise Concern" })}</h1>
              <p className="text-sm text-muted-foreground mt-1">{t("raiseConcern.subtitle", { defaultValue: "Securely report wrongdoing or misconduct." })}</p>
            </div>
          </div>
          <Button onClick={() => setCreateOpen(true)} className="bg-red-600 hover:bg-red-700 text-white gap-2">
            <AlertTriangle className="h-4 w-4" />
            {t("raiseConcern.buttonLabel", { defaultValue: "Report Concern" })}
          </Button>
        </div>

        <div className="flex items-center gap-2.5 p-3 rounded-lg bg-green-50 dark:bg-green-950/30 border border-green-200 dark:border-green-800 text-sm text-green-800 dark:text-green-300">
          <Shield className="h-4 w-4 shrink-0" />
          <p>{t("raiseConcern.anonymousNotice", { defaultValue: "Your reports are completely anonymous. HR cannot see who submitted this." })}</p>
        </div>

        <div className="pt-2">
          <h2 className="text-lg font-semibold mb-4">{t("raiseConcern.reportedCasesTab", { defaultValue: "My Reported Cases" })}</h2>

          <div className="space-y-4">
            {casesLoading && <ListSkeleton rows={3} />}
            {!casesLoading && myCases.length === 0 && (
              <Card>
                <CardContent className="p-12 text-center text-muted-foreground">
                  <ShieldAlert className="h-12 w-12 mx-auto mb-4 opacity-40" />
                  <p className="font-medium">{t("raiseConcern.noCases", { defaultValue: "No reported cases" })}</p>
                  <p className="text-sm mt-1">{t("raiseConcern.noCasesDesc", { defaultValue: "You have not submitted any concerns." })}</p>
                </CardContent>
              </Card>
            )}
            {!casesLoading && myCases.map((c) => {
              const cfg = CASE_STATUS_CONFIG[c.status] ?? CASE_STATUS_CONFIG.submitted;
              return (
                <Card
                  key={c.id}
                  className="cursor-pointer hover:shadow-md transition-all hover:-translate-y-0.5"
                  onClick={() => setSelectedCase(c)}
                >
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1.5">
                          <span className="text-xs font-mono text-muted-foreground">
                            {t("raiseConcern.caseId", { id: c.id, defaultValue: `Case #${c.id}` })}
                          </span>
                          <Badge className={`text-xs ${cfg.color}`}>
                            {t(`raiseConcern.${cfg.label}`, { defaultValue: cfg.label })}
                          </Badge>
                        </div>
                        <p className="text-sm font-medium truncate">
                          {t("raiseConcern.reported", { defaultValue: "Reported" })}:{" "}
                          <span className="text-foreground">{c.reportedEmployeeName}</span>
                          {c.reportedEmployeeTitle && (
                            <span className="text-muted-foreground"> · {c.reportedEmployeeTitle}</span>
                          )}
                        </p>
                        <p className="text-xs text-muted-foreground mt-0.5 line-clamp-1">{c.description}</p>
                      </div>
                      <div className="text-right shrink-0">
                        <p className="text-xs text-muted-foreground">{new Date(c.createdAt).toLocaleDateString()}</p>
                        {c.hrFeedback && (
                          <span className="text-xs text-primary font-medium mt-1 block">
                            {t("raiseConcern.hasUpdates", { defaultValue: "Has updates" })}
                          </span>
                        )}
                      </div>
                    </div>
                    {c.assignedToName && (
                      <p className="text-xs text-muted-foreground mt-2">
                        {t("raiseConcern.assignedTo", { defaultValue: "Assigned To" })}: {c.assignedToName}
                      </p>
                    )}
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </div>

      {/* ── Create Concern Dialog ── */}
      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className="sm:max-w-lg max-h-[85vh] flex flex-col overflow-hidden">
          <DialogHeader className="shrink-0">
            <DialogTitle className="flex items-center gap-2">
              <ShieldAlert className="h-5 w-5 text-red-500" />
              {t("raiseConcern.title", { defaultValue: "Raise Concern" })}
            </DialogTitle>
            <DialogDescription>{t("raiseConcern.subtitle", { defaultValue: "Securely report wrongdoing." })}</DialogDescription>
          </DialogHeader>
          <div className="flex-1 overflow-y-auto space-y-5 py-4">
            <div className="flex items-start gap-2.5 p-3 rounded-lg bg-green-50 dark:bg-green-950/30 border border-green-200 dark:border-green-800 text-sm text-green-800 dark:text-green-300">
              <Shield className="h-4 w-4 shrink-0 mt-0.5" />
              <div>
                <p className="font-semibold">{t("raiseConcern.anonymousBadge", { defaultValue: "100% Anonymous" })}</p>
                <p className="text-xs mt-0.5 opacity-80">{t("raiseConcern.anonymousGuarantee", { defaultValue: "Your identity is hidden." })}</p>
              </div>
            </div>
            <FloatWrapper label={t("raiseConcern.reportedEmployee", { defaultValue: "Reported Employee" })} required>
              <EmployeeCombobox
                employees={selectableEmployees}
                value={reportedEmployeeId}
                onChange={setReportedEmployeeId}
                placeholder={t("raiseConcern.selectEmployee", { defaultValue: "Search employee..." })}
              />
            </FloatWrapper>
            <FloatField
              label={t("raiseConcern.description", { defaultValue: "Description" })}
              type="textarea"
              required
              placeholder={t("raiseConcern.descriptionPlaceholder", { defaultValue: "Details..." })}
              value={description}
              onChange={setDescription}
            />
          </div>
          <DialogFooter className="shrink-0 pt-2">
            <Button variant="outline" onClick={() => setCreateOpen(false)}>{t("common.cancel", { defaultValue: "Cancel" })}</Button>
            <Button onClick={handleSubmitConcern} disabled={submitting || !reportedEmployeeId || !description.trim()} className="bg-red-600 hover:bg-red-700 text-white">
              {submitting ? t("raiseConcern.submitting", { defaultValue: "Submitting..." }) : t("raiseConcern.submit", { defaultValue: "Submit" })}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── Employee Case Detail Dialog ── */}
      <Dialog open={!!selectedCase} onOpenChange={(v) => { if (!v) { setSelectedCase(null); setReplyText(""); } }}>
        <DialogContent className="sm:max-w-2xl max-h-[88vh] flex flex-col overflow-hidden p-0">
          <DialogHeader className="px-6 py-4 border-b shrink-0">
            <div className="flex items-center justify-between">
              <DialogTitle className="flex items-center gap-2">
                <ShieldAlert className="h-5 w-5 text-orange-500" />
                {t("raiseConcern.caseDetail", { defaultValue: "Case Detail" })} —{" "}
                {selectedCase && t("raiseConcern.caseId", { id: selectedCase.id, defaultValue: `Case #${selectedCase.id}` })}
              </DialogTitle>
              {selectedCase && (
                <Badge className={`text-xs ${(CASE_STATUS_CONFIG[selectedCase.status] ?? CASE_STATUS_CONFIG.submitted).color}`}>
                  {t(`raiseConcern.${(CASE_STATUS_CONFIG[selectedCase.status] ?? CASE_STATUS_CONFIG.submitted).label}`, { defaultValue: "" })}
                </Badge>
              )}
            </div>
          </DialogHeader>

          {selectedCase && (() => {
            return (
              <div className="flex-1 flex flex-col overflow-hidden min-h-0">
                {/* Case meta + description */}
                <div className="px-6 py-4 space-y-3 border-b shrink-0">
                  <div className="grid grid-cols-2 gap-x-4 gap-y-1.5 text-sm">
                    <div>
                      <span className="text-xs text-muted-foreground">{t("raiseConcern.reported", { defaultValue: "Reported" })}</span>
                      <p className="font-medium">{selectedCase.reportedEmployeeName}</p>
                    </div>
                    <div>
                      <span className="text-xs text-muted-foreground">{t("raiseConcern.submittedOn", { defaultValue: "Submitted" })}</span>
                      <p className="font-medium">{new Date(selectedCase.createdAt).toLocaleDateString()}</p>
                    </div>
                    {selectedCase.assignedToName && (
                      <div className="col-span-2">
                        <span className="text-xs text-muted-foreground">{t("raiseConcern.assignedTo", { defaultValue: "Assigned To" })}</span>
                        <p className="font-medium">{selectedCase.assignedToName}</p>
                      </div>
                    )}
                  </div>
                  <div>
                    <Label className="text-xs text-muted-foreground">{t("raiseConcern.description", { defaultValue: "Description" })}</Label>
                    <p className="mt-1 text-sm bg-muted/30 rounded-lg px-3 py-2 whitespace-pre-wrap">{selectedCase.description}</p>
                  </div>
                  <div className="flex items-center gap-2 p-2 rounded-lg bg-green-50 dark:bg-green-950/30 border border-green-200 dark:border-green-800 text-xs text-green-700 dark:text-green-300">
                    <Shield className="h-3.5 w-3.5 shrink-0" />
                    {t("raiseConcern.anonymousNotice", { defaultValue: "Completely anonymous." })}
                  </div>
                </div>

                {/* Conversation */}
                <div className="flex-1 flex flex-col overflow-hidden min-h-0">
                  <div className="px-6 py-2.5 border-b shrink-0 flex items-center gap-1.5">
                    <MessageSquare className="h-3.5 w-3.5 text-muted-foreground" />
                    <span className="text-sm font-semibold">{t("raiseConcern.conversationThread", { defaultValue: "Conversation Thread" })}</span>
                  </div>
                  <div className="flex-1 overflow-y-auto px-6 py-4 bg-muted/20 min-h-0">
                    <ConcernThread rawMessages={selectedCase.hrFeedback} />
                  </div>
                  {/* Composer */}
                  <div className="px-6 py-4 border-t bg-background shrink-0 space-y-2.5">
                    <p className="text-xs text-muted-foreground">
                      {t("raiseConcern.additionalInfoHint", { defaultValue: "Add extra information. Your identity remains strictly anonymous." })}
                    </p>
                    <Textarea
                      className="min-h-[72px] resize-none"
                      placeholder={t("raiseConcern.additionalInfoPlaceholder", { defaultValue: "Add any extra information or clarification here..." })}
                      value={replyText}
                      onChange={(e) => setReplyText(e.target.value)}
                      onKeyDown={(e) => { if (e.key === "Enter" && (e.ctrlKey || e.metaKey)) handleSendReply(); }}
                    />
                    <div className="flex items-center justify-between gap-2 flex-wrap">
                      <div className="flex gap-2">
                        {selectedCase.status !== "resolved" && selectedCase.status !== "closed" && selectedCase.status !== "cancelled" && (
                          <Button variant="outline" size="sm" className="border-red-200 text-red-700 hover:bg-red-50 hover:text-red-800" onClick={() => setCancelDialogOpen(true)}>
                            {t("raiseConcern.cancelConcern", { defaultValue: "Cancel concern" })}
                          </Button>
                        )}
                        {(selectedCase.status === "resolved" || selectedCase.status === "closed") && (
                          <Button variant="outline" size="sm" className="border-amber-200 text-amber-700 hover:bg-amber-50" onClick={() => setReviewDialogOpen(true)}>
                            {t("raiseConcern.requestReview", { defaultValue: "I disagree with the resolution" })}
                          </Button>
                        )}
                      </div>
                      <Button size="sm" onClick={handleSendReply} disabled={sendingReply || !replyText.trim()}>
                        <Send className="h-3.5 w-3.5 mr-1.5" />
                        {sendingReply ? t("common.saving", { defaultValue: "Sending..." }) : t("raiseConcern.sendAdditionalInfo", { defaultValue: "Send" })}
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            );
          })()}
        </DialogContent>
      </Dialog>

      {/* ── Cancel concern dialog ── */}
      <Dialog open={cancelDialogOpen} onOpenChange={(open) => { setCancelDialogOpen(open); if (!open) setCancelReason(""); }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{t("raiseConcern.cancelConcernTitle", { defaultValue: "Cancel concern" })}</DialogTitle>
            <DialogDescription>{t("raiseConcern.cancelConcernDesc", { defaultValue: "You can cancel this concern before it is resolved or closed. Adding a reason is optional." })}</DialogDescription>
          </DialogHeader>
          <div>
            <FloatField
              label={t("raiseConcern.cancelReasonLabel", { defaultValue: "Reason (optional)" })}
              type="textarea"
              placeholder={t("raiseConcern.cancelReasonPlaceholder", { defaultValue: "Add a reason for cancelling..." })}
              value={cancelReason}
              onChange={setCancelReason}
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setCancelDialogOpen(false); setCancelReason(""); }}>{t("common.cancel", { defaultValue: "Cancel" })}</Button>
            <Button onClick={handleCancelConcern} disabled={cancellingCase} className="bg-red-600 hover:bg-red-700 text-white">
              {cancellingCase ? t("common.saving", { defaultValue: "Saving..." }) : t("raiseConcern.cancelConcernConfirm", { defaultValue: "Confirm cancellation" })}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── Request review dialog ── */}
      <Dialog open={reviewDialogOpen} onOpenChange={(open) => { setReviewDialogOpen(open); if (!open) setReviewRequestText(""); }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{t("raiseConcern.requestReviewTitle", { defaultValue: "Request another HR review" })}</DialogTitle>
            <DialogDescription>{t("raiseConcern.requestReviewDesc", { defaultValue: "Use this when you do not agree with the resolution. The case will move back to In Review and HR will be notified." })}</DialogDescription>
          </DialogHeader>
          <div>
            <FloatField
              label={t("raiseConcern.requestReviewMessageLabel", { defaultValue: "Details (optional)" })}
              type="textarea"
              placeholder={t("raiseConcern.requestReviewPlaceholder", { defaultValue: "Add any details about why you do not agree with the resolution..." })}
              value={reviewRequestText}
              onChange={setReviewRequestText}
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setReviewDialogOpen(false); setReviewRequestText(""); }}>{t("common.cancel", { defaultValue: "Cancel" })}</Button>
            <Button onClick={handleRequestReview} disabled={requestingReview}>
              {requestingReview ? t("common.saving", { defaultValue: "Saving..." }) : t("raiseConcern.requestReviewConfirm", { defaultValue: "Request review" })}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AppLayout>
  );
}
